/**
 * Phase 2 — keyframe playback math + v=1 → v=2 migration helpers.
 *
 * `samplePositions` is the read side of the multi-keyframe engine:
 * given a layout with keyframes (optionally walking a branch off a
 * parent keyframe), return the interpolated home + away token
 * positions at progress `t ∈ [0, 1]`. The 2D + 3D boards consume this
 * the same way they consume today's `linearArcLengthSample` for arrow
 * playback, so a single keyframe-aware code path replaces the legacy
 * "freeze on the snapshot" behaviour.
 *
 * `migrateLegacyToKeyframes` synthesises a single keyframe from a v=1
 * snapshot — existing boards see no change in render output, but the
 * keyframe-aware playback path is engaged uniformly.
 */
import type {
  TacticsLayout,
  TacticsKeyframe,
  TacticsBranch,
  TacticsPlayerSpot,
} from '@4cm/core/tactics';

/** Stable key for one token slot: "home-{index}" / "away-{index}".
 *  Index is by array position within the team's spots — the renderer
 *  uses the same convention for arrow `playerKey` bindings. */
export type PlayerKey = string;

/** Snapshot the current board state into a new TacticsKeyframe and
 *  append it to the existing keyframe list.
 *
 *  Convention: keyframes are evenly distributed across the [0, 1]
 *  range — `k[i].t = i / (N − 1)` for N ≥ 2, or `[0]` for the single
 *  snapshot case. Each call appends the snapshot as the LAST
 *  keyframe and re-normalises every t so the play stays in the
 *  [0, 1] envelope without the caller doing math. The board's
 *  walkthrough scrubber + samplePositions read t in [0, 1], so this
 *  matches their existing assumptions.
 *
 *  The new keyframe gets a fresh id + a default label (`Frame N`);
 *  callers wanting "Press trap", "Switch", etc. can rename after.
 *  Pass `phase` to tag the keyframe with a phase-of-play color.
 */
export interface CaptureKeyframeInput {
  homeSpots: TacticsPlayerSpot[];
  awaySpots: TacticsPlayerSpot[];
  arrows: TacticsLayout['arrows'];
  phase?: TacticsKeyframe['phase'];
  label?: string;
}

export function appendKeyframeSnapshot(
  existing: ReadonlyArray<TacticsKeyframe>,
  input: CaptureKeyframeInput,
): TacticsKeyframe[] {
  const id = `kf-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
  const label = input.label?.trim() || `Frame ${existing.length + 1}`;
  // Snapshot copies (don't share array refs with the live board so
  // subsequent drags don't mutate the captured frame).
  const snapshot: TacticsKeyframe = {
    id,
    // Placeholder t — gets overwritten by the redistribution pass below.
    t: 1,
    label,
    homeSpots: input.homeSpots.map((s) => ({ ...s })),
    awaySpots: input.awaySpots.map((s) => ({ ...s })),
    arrows: input.arrows.map((a) => ({
      ...a,
      points: a.points.map((p) => ({ ...p })),
    })),
    ...(input.phase ? { phase: input.phase } : {}),
  };
  const next = [...existing, snapshot];
  return redistributeKeyframeTimes(next);
}

/** Re-space `t` across [0, 1] so a list of N keyframes lands at
 *  evenly distributed positions. Stable wrt order — the i-th
 *  keyframe in the input gets t = i / (N − 1). */
export function redistributeKeyframeTimes(
  keyframes: ReadonlyArray<TacticsKeyframe>,
): TacticsKeyframe[] {
  if (keyframes.length === 0) return [];
  if (keyframes.length === 1) {
    return [{ ...keyframes[0]!, t: 0 }];
  }
  const last = keyframes.length - 1;
  return keyframes.map((k, i) => ({ ...k, t: i / last }));
}

export interface SampledPosition {
  x: number;
  z: number;
}

/** Lift a team's spot array into the keyed map used during interpolation. */
function spotsToMap(
  spots: TacticsPlayerSpot[],
  team: 'home' | 'away',
): Map<PlayerKey, SampledPosition> {
  const out = new Map<PlayerKey, SampledPosition>();
  for (let i = 0; i < spots.length; i++) {
    const s = spots[i];
    out.set(`${team}-${i}`, { x: s.x, z: s.z });
  }
  return out;
}

/** Linear interpolation between two scalar values. */
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Linear interpolation between two keyed position maps. Tokens
 *  missing from `next` inherit `prev`'s position (and vice versa) so a
 *  partial keyframe doesn't snap a token to origin. */
function interpolateMaps(
  prev: Map<PlayerKey, SampledPosition>,
  next: Map<PlayerKey, SampledPosition>,
  t: number,
): Map<PlayerKey, SampledPosition> {
  const out = new Map<PlayerKey, SampledPosition>();
  const keys = new Set<PlayerKey>([...prev.keys(), ...next.keys()]);
  for (const k of keys) {
    const a = prev.get(k);
    const b = next.get(k);
    if (a && b) out.set(k, { x: lerp(a.x, b.x, t), z: lerp(a.z, b.z, t) });
    else if (a) out.set(k, { ...a });
    else if (b) out.set(k, { ...b });
  }
  return out;
}

/** Pre-flatten a keyframe to its keyed position map. We do this once
 *  per resolved sequence so the per-frame sampler doesn't rebuild the
 *  Map on each scrub tick. */
interface FlatKeyframe {
  id: string;
  t: number;
  positions: Map<PlayerKey, SampledPosition>;
}

function flattenKeyframe(kf: TacticsKeyframe): FlatKeyframe {
  const home = spotsToMap(kf.homeSpots, 'home');
  const away = spotsToMap(kf.awaySpots, 'away');
  const positions = new Map<PlayerKey, SampledPosition>([
    ...home.entries(),
    ...away.entries(),
  ]);
  return { id: kf.id, t: kf.t, positions };
}

/** Build the linear keyframe timeline for the active path. If a branch
 *  is selected, the path is "main keyframes up to and including the
 *  parent, then the branch's own keyframes" — the parent keyframe
 *  anchors the fork so the visual transition is continuous. */
function resolveTimeline(
  keyframes: TacticsKeyframe[],
  branch: TacticsBranch | null,
): FlatKeyframe[] {
  if (!branch) {
    return [...keyframes].sort((a, b) => a.t - b.t).map(flattenKeyframe);
  }
  const parentIdx = keyframes.findIndex((k) => k.id === branch.parentKeyframeId);
  if (parentIdx < 0) {
    // Unknown parent — fall back to the main sequence so playback
    // still works rather than freezing.
    return [...keyframes].sort((a, b) => a.t - b.t).map(flattenKeyframe);
  }
  const prefix = keyframes.slice(0, parentIdx + 1);
  // Re-normalise the branch keyframes into the parent's [t, 1] window
  // so the scrubber walks the full alternate path inside that segment.
  const parentT = keyframes[parentIdx].t;
  const branchSpan = Math.max(0, 1 - parentT);
  const branchSorted = [...branch.keyframes].sort((a, b) => a.t - b.t);
  const remapped: TacticsKeyframe[] = branchSorted.map((k) => ({
    ...k,
    // Branch t is authored in [0, 1] within the branch; remap into the
    // parent-onwards window so the global scrubber still ranges [0, 1].
    t: parentT + branchSpan * Math.max(0, Math.min(1, k.t)),
  }));
  return [...prefix, ...remapped].map(flattenKeyframe);
}

/**
 * Sample interpolated token positions at progress `t ∈ [0, 1]`.
 *
 * Returns a map keyed by `${team}-${index}` so callers can map each
 * token to its render slot without re-flattening the keyframe arrays
 * per frame. Empty map when the layout has no keyframes (caller
 * should call `migrateLegacyToKeyframes` first to engage the path).
 *
 * `branchId` selects an alternate path rooted at one of the layout's
 * branches. Undefined / unknown id ⇒ main sequence.
 */
export function samplePositions(
  keyframes: TacticsKeyframe[],
  t: number,
  branchId?: string,
  branches?: TacticsBranch[],
): Map<PlayerKey, SampledPosition> {
  if (!keyframes || keyframes.length === 0) return new Map();
  const clampedT = Math.max(0, Math.min(1, t));

  const branch = branchId
    ? (branches ?? []).find((b) => b.id === branchId) ?? null
    : null;
  const timeline = resolveTimeline(keyframes, branch);
  if (timeline.length === 0) return new Map();
  if (timeline.length === 1) return new Map(timeline[0].positions);

  // Endpoints: avoid hunting for a segment when t is at the limits.
  if (clampedT <= timeline[0].t) return new Map(timeline[0].positions);
  const last = timeline[timeline.length - 1];
  if (clampedT >= last.t) return new Map(last.positions);

  // Find surrounding keyframes (binary search would be overkill — most
  // layouts have ≤ 5 keyframes).
  let prev = timeline[0];
  let next = timeline[timeline.length - 1];
  for (let i = 0; i < timeline.length - 1; i++) {
    const a = timeline[i];
    const b = timeline[i + 1];
    if (clampedT >= a.t && clampedT <= b.t) {
      prev = a;
      next = b;
      break;
    }
  }
  const span = next.t - prev.t;
  const local = span === 0 ? 0 : (clampedT - prev.t) / span;
  return interpolateMaps(prev.positions, next.positions, local);
}

/**
 * Synthesise a single keyframe from a v=1 snapshot so legacy layouts
 * play through the same keyframe-aware engine without a renderer
 * fork. The resulting keyframe's arrows are copied verbatim — they're
 * authored at layout level pre-Phase 2 and should still render
 * regardless of `t`.
 *
 * Returns the input layout unchanged when keyframes already exist —
 * the migration is idempotent so a board that's already on v=2 stays
 * on v=2 when this helper runs on its own output.
 */
export function migrateLegacyToKeyframes(layout: TacticsLayout): TacticsLayout {
  if (layout.keyframes && layout.keyframes.length > 0) return layout;
  const k0: TacticsKeyframe = {
    id: 'k0',
    t: 0,
    label: 'Start',
    homeSpots: layout.homeSpots,
    awaySpots: layout.awaySpots,
    arrows: layout.arrows,
  };
  return { ...layout, keyframes: [k0] };
}

/**
 * Convenience: extract the named-marker payload the keyframe scrubber
 * needs from a layout. Returns markers ordered by `t` so the HUD can
 * paint ticks left → right without sorting on every render.
 */
export function getKeyframeMarkers(
  keyframes: TacticsKeyframe[] | undefined,
): Array<{
  t: number;
  label: string;
  id: string;
  phase?: TacticsKeyframe['phase'];
}> {
  if (!keyframes || keyframes.length === 0) return [];
  return [...keyframes]
    .sort((a, b) => a.t - b.t)
    .map((k) => ({ t: k.t, label: k.label, id: k.id, phase: k.phase }));
}
